import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Tree } from 'antd'
import { DataNode } from 'antd/lib/tree'
import { sortBy, toLower } from 'lodash-es'
import { Key } from 'rc-tree/lib/interface'
import { CheckInfo } from 'rc-tree/lib/Tree'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'

import { IContinuousRoute, INetwork } from '$/types'
import { IFilterModalChildComponentProps } from 'app/layout/Navbar/Filters/FilterModal/types'
import {
  selectFilteredNetworkList,
  selectStagingProfile
} from 'app/layout/Navbar/Filters/globalFilterSelectors'
import {
  setEditInProgressSync,
  setStagingProfileContinousRoutesSync
} from 'app/layout/Navbar/Filters/globalFilterSlice'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { SearchInput } from 'common/components/SearchInput'
import StyledToggle from 'common/components/StyledToggle'

interface ITreeItemChildNode {
  key: string | number
  title?: React.ReactNode
}

//#region helpers
const lineToId = (line: IContinuousRoute): string => line.id.toString()

const deriveTreeItemsCount = (treeItems: DataNode[]) =>
  treeItems.reduce((accumulator, item) => {
    if (item.children) {
      accumulator += item.children.length
    }

    return accumulator
  }, 0)

const filterSelectedItems = (selectedLineIDs: Key[], nodes: DataNode[]) =>
  nodes
    .map((node) => ({
      ...node,
      children: node.children?.filter((childNode) =>
        selectedLineIDs.includes(childNode.key)
      )
    }))
    .filter((node) => node.children?.length)

const countSelectedItems = (
  selectedLineIds: Key[],
  nodes: ITreeItemChildNode[]
) =>
  nodes.reduce((accumulator: number, node: ITreeItemChildNode) => {
    // increment count via number coercion
    accumulator += Number(selectedLineIds.includes(node.key))

    return accumulator
  }, 0)

const deriveNodeKeys = (treeItems: DataNode[]) =>
  treeItems
    .flatMap((node) => node.children)
    .map((childNode) => childNode?.key as Key)
    .filter(Boolean)
//#endregion

const LineSelector = ({
  searchValue,
  setSearchValue,
  onlyShowSelected,
  setOnlyShowSelected
}: IFilterModalChildComponentProps) => {
  const dispatch = useDispatch()
  const stagingProfile = useSelector(selectStagingProfile)
  const filteredNetworks = useSelector(selectFilteredNetworkList)

  const {
    allNetworkLines,
    allNetworkKeys,
    allLineKeys,
    allTreeItems,
    allTreeItemsCount
  } = useMemo(() => {
    const allNetworkLines = filteredNetworks.flatMap(
      (network) => network.continuousRoutes
    )

    const allNetworkKeys: string[] = []
    const allLineKeys: string[] = []

    const _networkToTreeNode = (network: INetwork): DataNode => {
      const key = `network-${network.id}`

      allNetworkKeys.push(key)

      return {
        key,
        title: network.name,
        children: sortBy(
          network.continuousRoutes.map((line) => {
            const key = lineToId(line)

            allLineKeys.push(key)

            return {
              key,
              title: line.name
            }
          }),
          'title'
        )
      }
    }

    const allTreeItems: DataNode[] = sortBy(filteredNetworks, 'name').map(
      _networkToTreeNode
    )

    return {
      allNetworkLines,
      allNetworkKeys,
      allLineKeys,
      allTreeItems,
      allTreeItemsCount: deriveTreeItemsCount(allTreeItems)
    }
  }, [filteredNetworks])

  const selectedLineIds: Key[] =
    stagingProfile?.continuousRoutes.map(lineToId) || []

  const selectedCount = selectedLineIds.length
  const allLinesCount = allNetworkLines.length

  const [displayedTreeItems, setDisplayedTreeItems] =
    useState<DataNode[]>(allTreeItems)

  const [selectedTreeItems, setSelectedTreeItems] =
    useState<DataNode[]>(allTreeItems)

  const [expandedNodeKeys, setExpandedNodeKeys] = useState<Key[]>(allLineKeys)

  const displayedTreeItemsCount = useMemo(
    () => deriveTreeItemsCount(displayedTreeItems),
    [displayedTreeItems]
  )

  // update the state after memoization
  useEffect(() => {
    if (allTreeItemsCount) {
      setDisplayedTreeItems(allTreeItems)
    }
  }, [allTreeItemsCount, allTreeItems])

  // debounce searching since there can be tons of lines (consumers)
  useEffect(() => {
    if (!searchValue) {
      if (!onlyShowSelected) {
        setDisplayedTreeItems(allTreeItems)
        setExpandedNodeKeys(allNetworkKeys)
      }
    } else {
      const _id = setTimeout(() => {
        const searchedDisplayItems = allTreeItems
          .map((lineNode) => ({
            ...lineNode,
            children: lineNode.children?.filter((childNode) =>
              String(childNode.title)
                .toLowerCase()
                .includes(searchValue.toLowerCase())
            )
          }))
          .filter((node) => node.children?.length)

        setDisplayedTreeItems(searchedDisplayItems)

        setExpandedNodeKeys(
          searchedDisplayItems.map((displayedTreeItem) => displayedTreeItem.key)
        )
      }, 333)

      return () => clearTimeout(_id)
    }
  }, [allTreeItems, allNetworkKeys, allLineKeys, searchValue, onlyShowSelected])

  // prefill UI with initial continuousRoutes and stop reading them from redux

  const handleSearchInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
    },
    [setSearchValue]
  )

  const treeData = onlyShowSelected ? selectedTreeItems : displayedTreeItems

  const updateStagingProfile = useCallback(
    (lineIds: Key[]) => {
      dispatch(setEditInProgressSync(true))

      dispatch(
        setStagingProfileContinousRoutesSync(
          filteredNetworks
            .flatMap((network) => network.continuousRoutes)
            .filter((continuousRoute) =>
              lineIds.includes(String(continuousRoute.id))
            )
        )
      )
    },
    [dispatch, filteredNetworks]
  )

  const handleKeyCheck = (
    checkedKeys:
      | React.Key[]
      | {
          checked: React.Key[]
        },
    checkInfo: CheckInfo<DataNode>
  ) => {
    const selection = Array.isArray(checkedKeys)
      ? checkedKeys
      : checkedKeys.checked

    let unselected: Key[] = []

    const stagingProfileRoutes: Key[] =
      stagingProfile?.continuousRoutes.map((route) => String(route.id)) || []

    // determine if a network node was selected (adds an array of its children nodes instead of one node)
    if (!checkInfo.checked) {
      Array.isArray(checkInfo.node.children)
        ? (unselected = checkInfo.node.children.map((child: DataNode) => {
            return String(child.key)
          }))
        : unselected.push(String(checkInfo.node.key))
    }

    // filter out network node keys (like network-2)
    const lineIds = selection.filter(Number)

    updateStagingProfile(
      Array.from(new Set(stagingProfileRoutes.concat(lineIds))).filter(
        (route) => unselected.indexOf(route) === -1
      )
    )
  }

  const handleCheckboxChange = (
    event: React.ChangeEvent<{ checked: boolean }>
  ): void => {
    const { checked } = event.target

    if (stagingProfile) {
      if (checked) {
        updateStagingProfile(
          Array.from(
            new Set(
              stagingProfile?.continuousRoutes
                .map((route) => String(route.id) as Key)
                .concat(deriveNodeKeys(displayedTreeItems) ?? [])
            )
          )
        )
        setSelectedTreeItems(displayedTreeItems)
      } else {
        const nodeKeys = deriveNodeKeys(displayedTreeItems)

        updateStagingProfile(
          stagingProfile?.continuousRoutes
            .filter(
              (displayedKey) => nodeKeys.indexOf(String(displayedKey.id)) === -1
            )
            .map((route) => String(route.id)) ?? []
        )
        setSelectedTreeItems([])
        setOnlyShowSelected(false)
      }
    }
  }

  const handleOnlyShowSelectedToggle = () => {
    setOnlyShowSelected((onlyShowSelected) => !onlyShowSelected)
    setSearchValue('')
    setSelectedTreeItems(filterSelectedItems(selectedLineIds, allTreeItems))

    const searchedDisplayItems = allTreeItems
      .map((lineNode) => ({
        ...lineNode,
        children: lineNode.children?.filter((childNode) =>
          stagingProfile?.continuousRoutes.some(
            (route) => toLower(route.name) === toLower(String(childNode?.title))
          )
        )
      }))
      .filter((node) => node.children?.length)

    setDisplayedTreeItems(searchedDisplayItems)

    setExpandedNodeKeys(
      searchedDisplayItems.map((displayedTreeItem) => displayedTreeItem.key)
    )
  }

  const titleRender = (nodeData: DataNode) => {
    return (
      <div className='flex font-medium'>
        <span className='mr-3'>{nodeData.title as React.ReactNode}</span>
        {nodeData.children && (
          <span className='ml-auto mr-3 font-normal text-gray-700'>
            {countSelectedItems(
              selectedLineIds,
              nodeData.children as ITreeItemChildNode[]
            )}
          </span>
        )}
      </div>
    )
  }

  // prevent interactivity until redux is ready
  const reduxNotReady = !stagingProfile

  const checkIfAllSelected = (selectedLineNames: string[]) => {
    const displayedtreeChildren = displayedTreeItems.reduce(
      (accumulator: string[], item) => {
        if (item.children) {
          const childLines = item.children.map((child) => {
            return child.title as string
          })

          return accumulator.concat(childLines)
        }

        return accumulator
      },
      []
    )

    if (displayedtreeChildren.length < 1) {
      return false
    }

    for (const child of displayedtreeChildren) {
      if (selectedLineNames.indexOf(child) == -1) {
        return false
      }
    }

    return true
  }

  const isAllSelected =
    Boolean(selectedLineIds.length) &&
    checkIfAllSelected(
      stagingProfile?.continuousRoutes.map((route) => {
        return route.name as string
      }) ?? []
    )

  const checkboxDisabled = reduxNotReady || !displayedTreeItemsCount

  const selectAllLabel = `Select All${
    displayedTreeItemsCount === allTreeItemsCount ? '' : ' Searched'
  }`

  // auto expand collapsed nodes when selectedTreeItems changes
  useEffect(() => {
    setExpandedNodeKeys(() => selectedTreeItems.map((treeItem) => treeItem.key))
  }, [selectedTreeItems])

  // TODO: fix the 'Tree missing follow keys' warning on search clear
  //  Functionality isn't affected. seems like a cleanup issue
  //  assignees: JANA-itsaishvili
  const checkedKeys = searchValue
    ? displayedTreeItems
        .flatMap((treeItem) => treeItem.children)
        .map((dataNode) => String(dataNode?.key))
        .filter((displayedKey) => selectedLineIds.includes(displayedKey))
    : selectedLineIds

  return (
    <div
      className={[
        'flex flex-col',
        'w-[23rem] px-6 pb-4 pt-2',
        'border-l-2',
        'border-gray-200 bg-gray-100'
      ].join(' ')}
    >
      <div className='flex items-center justify-between'>
        <div className=''>
          Results <span className='font-semibold'>{allLinesCount}</span>
        </div>
        <div className='text-gray-700'>
          <label className='flex items-center' htmlFor='only-selected-toggle'>
            <span>Only show selected</span>
            <span className='ml-1 font-bold'>{selectedCount}</span>
            <StyledToggle
              id='only-selected-toggle'
              disabled={reduxNotReady}
              checked={onlyShowSelected}
              onChange={handleOnlyShowSelectedToggle}
            />
          </label>
        </div>
      </div>
      <div
        className={['mr-2 mb-2', onlyShowSelected ? 'opacity-50' : ''].join(
          ' '
        )}
      >
        <SearchInput
          className='flex-1'
          disabled={reduxNotReady || onlyShowSelected}
          placeholder='Search'
          value={searchValue}
          onChange={handleSearchInputChange}
        />
      </div>
      <div className='flex flex-1 overflow-auto bg-white p-1'>
        <OverlayScrollbar>
          <div className='ml-3 mr-1 flex items-center'>
            <FormControlLabel
              label={selectAllLabel}
              htmlFor='select-all-checkbox'
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                  lineHeight: '1.25rem'
                }
              }}
              control={
                <Checkbox
                  size='small'
                  id='select-all-checkbox'
                  className={'text-gray-700'.concat(
                    checkboxDisabled ? 'cursor-not-allowed' : ''
                  )}
                  name={selectAllLabel}
                  checked={isAllSelected}
                  value={isAllSelected}
                  disabled={checkboxDisabled}
                  onChange={handleCheckboxChange}
                />
              }
            />
            <div className='flex-1' />
            <span className='mr-3 text-gray-700'>
              {displayedTreeItemsCount}
            </span>
          </div>
          <hr className='mx-1 mb-2' />
          <Tree
            autoExpandParent
            defaultExpandParent
            height={318}
            disabled={reduxNotReady}
            className='mr-1 font-base'
            checkable={!onlyShowSelected}
            titleRender={titleRender}
            treeData={treeData}
            checkedKeys={checkedKeys}
            expandedKeys={expandedNodeKeys}
            onCheck={handleKeyCheck}
            onExpand={setExpandedNodeKeys}
          />
        </OverlayScrollbar>
        {allTreeItemsCount === 0 && (
          <div className='mt-32 w-full text-center'>
            No results available for the selection.
          </div>
        )}
      </div>
    </div>
  )
}

export default LineSelector
